// Expenses — money OUT (Priority Pest Defense).
//
// Same four hand-entered collections, fields and mirroring into
// "transactions" as before:
//   subcontractorPay  (Contractor Pay page writes here too)
//   employeePay
//   monthlyBills      (Cash Plan and Accounting Exports read these)
//   bankDeductions    (Accounting Exports reads these)
// plus a read-only "Tech Daily Pay" section from dailyLabor
// (utils/techDailyPay.js), which counts as payroll in the totals.
//
// No double counting: an Employee Pay entry that pays the same person's Tech
// Daily Pay days is shown but NOT added (utils/expensesDailyPayRule.js).

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./FinancePages.css";
import * as XLSX from "xlsx";
import DepositsFinModal from "../components/depositsFinModal";
import {
  BULK_TEMPLATES,
  todayStr,
  parseAmount,
  formatMoney,
  formatDateMDY,
  sortByDateDesc,
  upsertTransaction,
  deleteLinkedTransaction,
  buildTxFromSubPay,
  buildTxFromEmpPay,
  buildTxFromBill,
  buildTxFromBankDed,
} from "../utils/financeEntries";
import { fetchDailyLaborDays, summarizeDailyLabor, usersToMap, staffName, isDeactivated } from "../utils/techDailyPay";
import { COVERAGE_OPTIONS, buildDailyPayIndex, employeePayCoverage, normPerson } from "../utils/expensesDailyPayRule";

const effectLabel = (effect) => (effect === "add" ? "Increase" : "Decrease");

/** Excel dates arrive as serial numbers or M/D/YYYY text; store YYYY-MM-DD. */
const normalizeSheetDate = (value) => {
  if (value === undefined || value === null || value === "") return todayStr();
  if (typeof value === "number") {
    const d = XLSX.SSF.parse_date_code(value);
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const year = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${year}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  }
  return s;
};

const DAILY_PAY_RULE =
  "Tech Daily Pay counts as payroll. A Regular or Overtime Employee Pay entry for someone who has Tech Daily Pay in the same month is treated as the paycheck for those days and is not added again; bonuses and other pay are added (change it per entry with Edit).";

/* One definition per tab drives the table, the form and bulk upload. */
const TABS = {
  subPay: {
    label: "Subcontractor Pay",
    collection: "subcontractorPay",
    buildTx: buildTxFromSubPay,
    addLabel: "Add Subcontractor Pay",
    note:
      "Payments made on Contractor Pay and the Job Estimate Spreadsheet show here too. When the bank statement is uploaded, each one is matched to its bank line so it's only counted once.",
    empty: () => ({ property: "", amount: "", description: "", category: "Labor", effect: "subtract", date: todayStr() }),
    fields: [
      { name: "property", label: "Property / Sub", type: "property", full: true },
      { name: "amount", label: "Amount", type: "amount", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "category", label: "Category", type: "select", options: ["Labor", "Materials Reimbursed", "Travel", "Other"] },
      { name: "effect", label: "Effect on Totals", type: "effect" },
      { name: "description", label: "Description", type: "text", full: true },
    ],
    columns: [
      { key: "date", label: "Date", date: true },
      { key: "property", label: "Property / Sub", strong: true },
      { key: "category", label: "Category" },
      { key: "description", label: "Description", truncate: true },
      { key: "effect", label: "Effect", render: (r) => effectLabel(r.effect) },
      { key: "amount", label: "Amount", money: true },
    ],
    fromSheet: (r) => ({
      property: r.Property || "",
      amount: r.Amount || "",
      category: r.Category || "Labor",
      description: r.Description || "",
      effect: r.Effect || "subtract",
      date: normalizeSheetDate(r.Date),
    }),
  },
  empPay: {
    label: "Employee Pay",
    collection: "employeePay",
    buildTx: buildTxFromEmpPay,
    addLabel: "Add Employee Pay",
    note: DAILY_PAY_RULE,
    empty: () => ({
      employeeName: "", property: "", amount: "", description: "", payType: "Regular", effect: "subtract", date: todayStr(), dailyPayCoverage: "auto",
    }),
    fields: [
      { name: "employeeName", label: "Employee Name", type: "employee", full: true },
      { name: "property", label: "Property", type: "property", full: true },
      { name: "amount", label: "Amount", type: "amount", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "payType", label: "Pay Type", type: "select", options: ["Regular", "Overtime", "Bonus", "Other"] },
      { name: "effect", label: "Effect on Totals", type: "effect" },
      { name: "dailyPayCoverage", label: "Tech Daily Pay overlap", type: "select", options: COVERAGE_OPTIONS, full: true },
      { name: "description", label: "Description", type: "text", full: true },
    ],
    columns: [
      { key: "date", label: "Date", date: true },
      { key: "employeeName", label: "Employee", strong: true },
      { key: "property", label: "Property" },
      { key: "payType", label: "Pay Type" },
      { key: "description", label: "Description", truncate: true },
      { key: "effect", label: "Effect", render: (r) => effectLabel(r.effect) },
      { key: "amount", label: "Amount", money: true },
    ],
    fromSheet: (r) => ({
      employeeName: r.Employee || "",
      property: r.Property || "",
      amount: r.Amount || "",
      payType: r["Pay Type"] || "Regular",
      description: r.Description || "",
      effect: r.Effect || "subtract",
      date: normalizeSheetDate(r.Date),
      dailyPayCoverage: "auto",
    }),
  },
  bills: {
    label: "Monthly Bills",
    collection: "monthlyBills",
    buildTx: buildTxFromBill,
    addLabel: "Add Monthly Bill",
    note: "Bills entered here also feed the Cash Plan.",
    empty: () => ({ property: "", vendor: "", amount: "", billType: "Utilities", description: "", effect: "subtract", date: todayStr() }),
    fields: [
      { name: "vendor", label: "Vendor", type: "text", full: true },
      { name: "amount", label: "Amount", type: "amount", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      {
        name: "billType",
        label: "Bill Type",
        type: "select",
        options: [
          ["Utilities", "Utilities"], ["Insurance", "Insurance"], ["Loan", "Loan"], ["Lease", "Lease"], ["Rent", "Rent"],
          ["Vehicle", "Vehicle"], ["Phone", "Phone"], ["Subscription", "Subscription"], ["AssetPurchase", "Asset Purchase"],
          ["CreditCard", "Credit Card"], ["Chemicals", "Chemicals / Supplies"], ["Other", "Other"],
        ],
      },
      { name: "effect", label: "Effect on Totals", type: "effect" },
      { name: "description", label: "Description", type: "text", full: true },
    ],
    columns: [
      { key: "date", label: "Date", date: true },
      { key: "vendor", label: "Vendor", strong: true },
      { key: "billType", label: "Bill Type" },
      { key: "description", label: "Description", truncate: true },
      { key: "effect", label: "Effect", render: (r) => effectLabel(r.effect) },
      { key: "amount", label: "Amount", money: true },
    ],
    fromSheet: (r) => ({
      vendor: r.Vendor || "",
      amount: r.Amount || "",
      billType: r["Bill Type"] || "Utilities",
      description: r.Description || "",
      effect: r.Effect || "subtract",
      date: normalizeSheetDate(r.Date),
    }),
  },
  bankDed: {
    label: "Bank Deductions",
    collection: "bankDeductions",
    buildTx: buildTxFromBankDed,
    addLabel: "Add Bank Deduction",
    note:
      "Fees on a bank statement you upload on the Bank Statement page are already counted there. Only add a fee here if it isn't on an uploaded statement, or it will be counted twice.",
    warn: true,
    empty: () => ({
      bankName: "", amount: "", deductionType: "Service Fee", statementMonth: "", statementCategory: "", description: "", effect: "subtract", date: todayStr(),
    }),
    fields: [
      { name: "bankName", label: "Bank Name", type: "text", full: true },
      { name: "amount", label: "Amount", type: "amount", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "deductionType", label: "Deduction Type", type: "select", options: ["Service Fee", "Transfer Adjustment", "Returned Item", "Other"] },
      { name: "statementMonth", label: "Statement Month", type: "month" },
      {
        name: "statementCategory",
        label: "Statement Category",
        type: "select",
        options: [["", "—"], ["Deposits", "Deposits"], ["Checks", "Checks"], ["Bank Fees", "Bank Fees"], ["Interest", "Interest"], ["Adjustments", "Adjustments"]],
      },
      { name: "effect", label: "Effect on Totals", type: "effect" },
      { name: "description", label: "Description", type: "text", full: true },
    ],
    columns: [
      { key: "date", label: "Date", date: true },
      { key: "bankName", label: "Bank", strong: true },
      { key: "deductionType", label: "Type" },
      { key: "statementMonth", label: "Statement" },
      { key: "description", label: "Description", truncate: true },
      { key: "effect", label: "Effect", render: (r) => effectLabel(r.effect) },
      { key: "amount", label: "Amount", money: true },
    ],
    fromSheet: (r) => ({
      bankName: r.Bank || "",
      amount: r.Amount || "",
      deductionType: r.Type || "Service Fee",
      statementMonth: r["Statement Month"] || "",
      statementCategory: r.Category || "",
      description: r.Description || "",
      effect: r.Effect || "subtract",
      date: normalizeSheetDate(r.Date),
    }),
  },
};
const TAB_KEYS = Object.keys(TABS);
const TECH_TAB = "techPay";
const ALL_TABS = [...TAB_KEYS, TECH_TAB];

// Money out counts positive; an "Increase" row (refund / credit) reduces it.
const signed = (r) => (r.effect === "add" ? -1 : 1) * parseAmount(r.amount);
const netOut = (list) => list.reduce((s, r) => s + signed(r), 0);

const normalizeSubPay = (d) => {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    amount: parseAmount(data.amount ?? data.amountPaid),
    amountPaid: parseAmount(data.amountPaid ?? data.amount),
    date:
      data.date ||
      (data.datePaid?.toDate ? data.datePaid.toDate().toISOString().slice(0, 10) : String(data.datePaid || data.createdAt || "").slice(0, 10)),
    property: data.property || data.subcontractorName || data.userName || data.vendor || "Subcontractor",
    vendor: data.vendor || data.subcontractorName || data.userName || data.property || "Subcontractor",
    category: data.category || "Subcontractor",
    effect: data.effect || "subtract",
  };
};

const Expenses = ({ onOpenTab } = {}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("subPay");
  const [rows, setRows] = useState({ subPay: [], empPay: [], bills: [], bankDed: [] });
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [laborDays, setLaborDays] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filterProperty, setFilterProperty] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  const [form, setForm] = useState(null); // current tab's draft (has id when editing)
  const [isSaving, setIsSaving] = useState(false);

  const [showBulk, setShowBulk] = useState(false);
  const [bulkType, setBulkType] = useState("subPay");
  const [bulkRows, setBulkRows] = useState([]);
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [subSnap, empSnap, billSnap, bankSnap, propSnap, usersSnap, days] = await Promise.all([
          getDocs(collection(db, "subcontractorPay")),
          getDocs(collection(db, "employeePay")),
          getDocs(collection(db, "monthlyBills")),
          getDocs(collection(db, "bankDeductions")),
          getDocs(collection(db, "properties")),
          getDocs(collection(db, "users")),
          fetchDailyLaborDays().catch((err) => {
            console.error("Could not load Tech Daily Pay:", err);
            return [];
          }),
        ]);
        const plain = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows({
          subPay: sortByDateDesc(subSnap.docs.map(normalizeSubPay)),
          empPay: sortByDateDesc(plain(empSnap)),
          bills: sortByDateDesc(plain(billSnap)),
          bankDed: sortByDateDesc(plain(bankSnap)),
        });
        setProperties(plain(propSnap).sort((a, b) => (a.propertyName || "").localeCompare(b.propertyName || "")));
        setUsers(plain(usersSnap));
        setLaborDays(days);
      } catch (err) {
        console.error("Error loading expenses:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const def = TABS[tab] || null;
  const usersById = useMemo(() => usersToMap(users), [users]);
  const staffNames = useMemo(
    () =>
      users
        .filter((u) => !isDeactivated(u))
        .map((u) => staffName(u))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [users]
  );

  // Who had Tech Daily Pay in which month — all dates, so an entry near the
  // edge of the filter still sees its whole month.
  const dailyIndex = useMemo(() => {
    const all = summarizeDailyLabor(laborDays, usersById);
    return buildDailyPayIndex(all.rows, usersById);
  }, [laborDays, usersById]);

  // Tech Daily Pay for the selected period.
  const techPay = useMemo(() => {
    const summary = summarizeDailyLabor(laborDays, usersById, filterStart, filterEnd);
    const q = search.trim().toLowerCase();
    const people = Object.entries(summary.byUser)
      .map(([userId, v]) => ({ userId, ...v, name: v.name || staffName(usersById[userId] || {}) || "Unknown" }))
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .sort((a, b) => b.amount - a.amount);
    const total = q ? people.reduce((s, p) => s + p.amount, 0) : summary.total;
    return { summary, people, total, dayCount: Object.keys(summary.byDate).length };
  }, [laborDays, usersById, filterStart, filterEnd, search]);

  const coverageOf = (row) => employeePayCoverage(row, dailyIndex);

  const passes = (item) => {
    if (filterProperty && item.property && item.property !== filterProperty) return false;
    if (filterStart || filterEnd) {
      const dt = new Date(item.date);
      if (filterStart && dt < new Date(filterStart)) return false;
      if (filterEnd && dt > new Date(filterEnd)) return false;
    }
    if (search.trim()) {
      const hay = Object.values(item).filter((v) => typeof v === "string" || typeof v === "number").join(" ").toLowerCase();
      if (!hay.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  };

  const filtered = useMemo(() => {
    const out = {};
    TAB_KEYS.forEach((k) => (out[k] = rows[k].filter(passes)));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, filterProperty, filterStart, filterEnd]);

  const totals = useMemo(() => {
    const t = {};
    TAB_KEYS.forEach((k) => (t[k] = netOut(filtered[k])));
    // Employee pay that only re-pays Tech Daily Pay days is left out.
    const empCounted = filtered.empPay.filter((r) => coverageOf(r).counted);
    t.empPay = netOut(empCounted);
    t.empCovered = netOut(filtered.empPay.filter((r) => !coverageOf(r).counted));
    t.empCoveredCount = filtered.empPay.length - empCounted.length;
    t[TECH_TAB] = techPay.total;
    t.all = TAB_KEYS.reduce((s, k) => s + t[k], 0) + t[TECH_TAB];
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, techPay, dailyIndex]);

  const hasFilters = search || filterProperty || filterStart || filterEnd;
  const periodLabel =
    filterStart || filterEnd
      ? `${filterStart ? formatDateMDY(filterStart) : "…"} – ${filterEnd ? formatDateMDY(filterEnd) : "…"}`
      : "All dates";

  /* ------------------------- add / edit / delete ------------------------ */

  const openAdd = () => def && setForm(def.empty());
  const openEdit = (row) => {
    const draft = { ...def.empty() };
    def.fields.forEach((f) => {
      if (row[f.name] !== undefined && row[f.name] !== null) draft[f.name] = row[f.name];
    });
    setForm({ ...draft, id: row.id, txId: row.txId || null, __original: row });
  };

  const linkUserId = (data) => {
    if (tab !== "empPay") return data;
    const match = users.find((u) => normPerson(staffName(u)) && normPerson(staffName(u)) === normPerson(data.employeeName));
    return { ...data, userId: match?.id || "" };
  };

  const saveForm = async () => {
    if (!form.amount || !form.date) {
      alert("Amount and Date are required.");
      return;
    }
    setIsSaving(true);
    try {
      const { id, txId: oldTxId, __original, ...draft } = form;
      if (id) {
        // Edit: only the fields this form shows are written back.
        const patch = { amount: parseAmount(draft.amount), updatedAt: new Date().toISOString() };
        def.fields.forEach((f) => {
          if (f.name !== "amount") patch[f.name] = draft[f.name] ?? "";
        });
        if (tab === "subPay" && __original && __original.amountPaid !== undefined) patch.amountPaid = patch.amount;
        const finalPatch = linkUserId(patch);
        await updateDoc(doc(db, def.collection, id), finalPatch);
        const merged = { ...__original, ...finalPatch };
        if (oldTxId) {
          await upsertTransaction({ txId: oldTxId, payload: def.buildTx({ ...merged, id }) });
        }
        setRows((prev) => ({ ...prev, [tab]: sortByDateDesc(prev[tab].map((r) => (r.id === id ? merged : r))) }));
      } else {
        const data = linkUserId({ ...draft, amount: parseAmount(draft.amount), createdFrom: "Expenses", createdAt: new Date().toISOString() });
        const ref = await addDoc(collection(db, def.collection), data);
        const txId = await upsertTransaction({ txId: null, payload: def.buildTx({ id: ref.id, ...data }) });
        await updateDoc(doc(db, def.collection, ref.id), { txId });
        setRows((prev) => ({ ...prev, [tab]: sortByDateDesc([...prev[tab], { id: ref.id, ...data, txId }]) }));
      }
      setForm(null);
    } catch (err) {
      console.error(err);
      alert("Could not save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const what = row.vendor || row.employeeName || row.bankName || row.property || "this entry";
    if (!window.confirm(`Delete ${formatMoney(parseAmount(row.amount))} for ${what}? It will also be removed from Financials.`)) return;
    try {
      await deleteDoc(doc(db, def.collection, row.id));
      await deleteLinkedTransaction({ txId: row.txId, sourceCollection: def.collection, sourceId: row.id });
      setRows((prev) => ({ ...prev, [tab]: prev[tab].filter((r) => r.id !== row.id) }));
    } catch (err) {
      console.error(err);
      alert("Could not delete this entry.");
    }
  };

  /* ---------------------------- bulk upload --------------------------- */

  const downloadTemplate = () => {
    const t = BULK_TEMPLATES[bulkType];
    const ws = XLSX.utils.aoa_to_sheet([t.headers, Array(t.headers.length).fill("")]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, t.filename);
  };

  const parseBulkFile = async (file) => {
    if (!file) return;
    const wb = XLSX.read(await file.arrayBuffer());
    const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
    setBulkRows(raw.filter((r) => r.Amount !== undefined && r.Amount !== "").map((r) => TABS[bulkType].fromSheet(r)));
  };

  const saveBulk = async () => {
    if (!bulkRows.length) return;
    const t = TABS[bulkType];
    setIsBulkSaving(true);
    try {
      const added = [];
      for (const r of bulkRows) {
        let data = { ...r, amount: parseAmount(r.amount), createdFrom: "ExpensesBulkUpload", createdAt: new Date().toISOString() };
        if (bulkType === "empPay") {
          const match = users.find((u) => normPerson(staffName(u)) && normPerson(staffName(u)) === normPerson(data.employeeName));
          data = { ...data, userId: match?.id || "" };
        }
        const ref = await addDoc(collection(db, t.collection), data);
        const txId = await upsertTransaction({ txId: null, payload: t.buildTx({ id: ref.id, ...data }) });
        await updateDoc(doc(db, t.collection, ref.id), { txId });
        added.push({ id: ref.id, ...data, txId });
      }
      setRows((prev) => ({ ...prev, [bulkType]: sortByDateDesc([...prev[bulkType], ...added]) }));
      setBulkRows([]);
      setShowBulk(false);
      setTab(bulkType);
    } catch (err) {
      console.error(err);
      alert("Bulk save failed partway. Refresh to see what was saved.");
    } finally {
      setIsBulkSaving(false);
    }
  };

  /* ------------------------------ render ------------------------------ */

  const renderField = (f) => {
    const value = form[f.name] ?? "";
    const onChange = (e) => setForm((p) => ({ ...p, [f.name]: e.target.value }));
    let input;
    if (f.type === "property") {
      input = (
        <select value={value} onChange={onChange}>
          <option value="">Select property</option>
          {value && !properties.some((p) => p.propertyName === value) && <option value={value}>{value}</option>}
          {properties.map((p) => (
            <option key={p.id} value={p.propertyName}>{p.propertyName}</option>
          ))}
        </select>
      );
    } else if (f.type === "employee") {
      input = (
        <>
          <input type="text" list="expenses-staff-names" value={value} onChange={onChange} placeholder="Pick or type a name" />
          <datalist id="expenses-staff-names">
            {staffNames.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </>
      );
    } else if (f.type === "select") {
      input = (
        <select value={value} onChange={onChange}>
          {f.options.map((o) => {
            const [v, l] = Array.isArray(o) ? o : [o, o];
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
      );
    } else if (f.type === "effect") {
      input = (
        <select value={value} onChange={onChange}>
          <option value="subtract">Decrease Total</option>
          <option value="add">Increase Total</option>
        </select>
      );
    } else {
      const type = f.type === "amount" ? "number" : f.type === "text" ? "text" : f.type;
      input = <input type={type} step={f.type === "amount" ? "0.01" : undefined} value={value} onChange={onChange} />;
    }
    return (
      <label key={f.name} className={`fin-field ${f.full ? "fin-field--full" : ""}`}>
        <span>{f.label}{f.required && <em> *</em>}</span>
        {input}
      </label>
    );
  };

  const list = def ? filtered[tab] : [];
  const tabCount = (k) => (k === TECH_TAB ? techPay.people.length : filtered[k].length);
  const tabSub = (k) => {
    if (k === TECH_TAB) return `${techPay.dayCount} day${techPay.dayCount === 1 ? "" : "s"} · ${techPay.people.length} tech${techPay.people.length === 1 ? "" : "s"}`;
    const n = filtered[k].length;
    const base = `${n} entr${n === 1 ? "y" : "ies"}${hasFilters ? " (filtered)" : ""}`;
    if (k === "empPay" && totals.empCoveredCount) return `${base} · ${totals.empCoveredCount} covered by daily pay`;
    return base;
  };
  const tabLabel = (k) => (k === TECH_TAB ? "Tech Daily Pay" : TABS[k].label);

  return (
    <div className="fin-page">
      <header className="fin-header">
        <div>
          <h1 className="fin-title">Expenses</h1>
          <p className="fin-subtitle">
            Subcontractor pay, employee pay, monthly bills, bank deductions and Tech Daily Pay payroll · {periodLabel}:{" "}
            <b>{formatMoney(totals.all)}</b>
          </p>
        </div>
        <div className="fin-header__actions">
          {tab === TECH_TAB ? (
            <button className="fin-btn fin-btn--primary" onClick={() => (onOpenTab ? onOpenTab("dailypay") : navigate("/DailyLabor"))}>Open Tech Daily Pay</button>
          ) : (
            <>
              <button className="fin-btn fin-btn--secondary" onClick={() => { setBulkType(tab); setBulkRows([]); setShowBulk(true); }}>
                Bulk Upload
              </button>
              <button className="fin-btn fin-btn--primary" onClick={openAdd}>+ {def.addLabel}</button>
            </>
          )}
        </div>
      </header>

      <section className="fin-stats" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
        {ALL_TABS.map((k) => (
          <button key={k} className={`fin-stat fin-stat--tab ${tab === k ? "is-active" : ""}`} onClick={() => setTab(k)}>
            <span className="fin-stat__label">{tabLabel(k)}</span>
            <span className="fin-stat__value">{formatMoney(totals[k])}</span>
            <span className="fin-stat__sub">{tabSub(k)}</span>
          </button>
        ))}
      </section>

      <section className="fin-card">
        <div className="fin-tabs" role="tablist">
          {ALL_TABS.map((k) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "is-active" : ""} onClick={() => setTab(k)}>
              {tabLabel(k)}
            </button>
          ))}
        </div>

        {tab === TECH_TAB ? (
          <div className="fin-note fin-note--inline">
            Read only — from the Tech Daily Pay page (one saved day per date; hours from Clock In/Out, pay from each tech's Staff
            rate). {DAILY_PAY_RULE}
          </div>
        ) : (
          def.note && <div className={`fin-note fin-note--inline ${def.warn ? "fin-note--warn" : ""}`}>{def.note}</div>
        )}

        <div className="fin-toolbar">
          <input className="fin-input fin-input--search" type="search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {(tab === "subPay" || tab === "empPay") && (
            <select className="fin-input" value={filterProperty} onChange={(e) => setFilterProperty(e.target.value)}>
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.propertyName}>{p.propertyName}</option>
              ))}
            </select>
          )}
          <label className="fin-inline">
            <span>From</span>
            <input className="fin-input" type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} />
          </label>
          <label className="fin-inline">
            <span>To</span>
            <input className="fin-input" type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} />
          </label>
          {hasFilters && (
            <button className="fin-linkbtn" onClick={() => { setSearch(""); setFilterProperty(""); setFilterStart(""); setFilterEnd(""); }}>
              Clear filters
            </button>
          )}
        </div>

        {tab === TECH_TAB ? (
          <div className="fin-table-wrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Tech</th>
                  <th className="fin-num">Days worked</th>
                  <th className="fin-num">Hours</th>
                  <th className="fin-num">Pay</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4} className="fin-empty">Loading…</td></tr>
                )}
                {!loading && techPay.people.length === 0 && (
                  <tr><td colSpan={4} className="fin-empty">No Tech Daily Pay saved for {periodLabel.toLowerCase()}.</td></tr>
                )}
                {techPay.people.map((p) => (
                  <tr key={p.userId}>
                    <td className="fin-strong">{p.name}</td>
                    <td className="fin-num">{p.days}</td>
                    <td className="fin-num">{Math.round(p.hours * 100) / 100}</td>
                    <td className="fin-num fin-strong">{formatMoney(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
              {techPay.people.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="fin-strong">Total</td>
                    <td className="fin-num">{techPay.people.reduce((s, p) => s + p.days, 0)}</td>
                    <td className="fin-num">{Math.round(techPay.people.reduce((s, p) => s + p.hours, 0) * 100) / 100}</td>
                    <td className="fin-num fin-strong">{formatMoney(techPay.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <div className="fin-table-wrap">
            <table className="fin-table">
              <thead>
                <tr>
                  {def.columns.map((c) => (
                    <th key={c.key} className={c.money ? "fin-num" : ""}>{c.label}</th>
                  ))}
                  {tab === "empPay" && <th>In totals</th>}
                  <th className="fin-right"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={def.columns.length + 2} className="fin-empty">Loading…</td></tr>
                )}
                {!loading && list.length === 0 && (
                  <tr><td colSpan={def.columns.length + 2} className="fin-empty">No {def.label.toLowerCase()} entries{hasFilters ? " match the filters" : " yet"}.</td></tr>
                )}
                {list.map((r) => {
                  const cov = tab === "empPay" ? coverageOf(r) : null;
                  return (
                    <tr key={r.id} className={cov && !cov.counted ? "is-muted" : ""}>
                      {def.columns.map((c) => {
                        const val = c.render ? c.render(r) : r[c.key];
                        return (
                          <td
                            key={c.key}
                            className={[c.money ? "fin-num fin-strong" : "", c.strong ? "fin-strong" : "", c.truncate ? "fin-truncate" : "", c.date ? "fin-nowrap" : ""].join(" ")}
                            title={c.truncate ? String(val || "") : undefined}
                          >
                            {c.money ? formatMoney(parseAmount(val)) : c.date ? formatDateMDY(val) : val}
                          </td>
                        );
                      })}
                      {cov && (
                        <td className="fin-small">
                          {cov.counted ? (
                            <span>{cov.reason || "Added"}</span>
                          ) : (
                            <span className="fin-tag" title="Not added — Tech Daily Pay already counts these days">{cov.reason}</span>
                          )}
                        </td>
                      )}
                      <td className="fin-right fin-nowrap">
                        <button className="fin-btn fin-btn--ghost fin-btn--sm" onClick={() => openEdit(r)}>Edit</button>
                        <button className="fin-btn fin-btn--ghost-danger fin-btn--sm" onClick={() => handleDelete(r)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --------------------------- Add / edit form --------------------------- */}
      {form && def && (
        <DepositsFinModal onClose={() => setForm(null)} size="md">
          <div className="fin-modal__head">
            <h2>{form.id ? `Edit ${def.label}` : def.addLabel}</h2>
            <button className="fin-icon-btn" onClick={() => setForm(null)} aria-label="Close">×</button>
          </div>
          <div className="fin-modal__body">
            {def.warn && <div className="fin-note fin-note--warn fin-note--sm">{def.note}</div>}
            {tab === "empPay" && <div className="fin-note fin-note--sm">{DAILY_PAY_RULE}</div>}
            <div className="fin-form">{def.fields.map(renderField)}</div>
          </div>
          <div className="fin-modal__foot">
            <button className="fin-btn fin-btn--secondary" onClick={() => setForm(null)}>Cancel</button>
            <button className="fin-btn fin-btn--primary" onClick={saveForm} disabled={isSaving}>{isSaving ? "Saving…" : "Save"}</button>
          </div>
        </DepositsFinModal>
      )}

      {/* ----------------------------- Bulk upload ---------------------------- */}
      {showBulk && (
        <DepositsFinModal onClose={() => !isBulkSaving && setShowBulk(false)} size="lg">
          <div className="fin-modal__head">
            <div>
              <h2>Bulk Upload Expenses</h2>
              <p className="fin-modal__sub">Columns: {BULK_TEMPLATES[bulkType].headers.join(" · ")}</p>
            </div>
            <button className="fin-icon-btn" onClick={() => setShowBulk(false)} aria-label="Close">×</button>
          </div>
          <div className="fin-modal__body">
            <div className="fin-upload-row">
              <select className="fin-input" value={bulkType} onChange={(e) => { setBulkType(e.target.value); setBulkRows([]); }}>
                {TAB_KEYS.map((k) => (
                  <option key={k} value={k}>{TABS[k].label}</option>
                ))}
              </select>
              <button className="fin-btn fin-btn--secondary fin-btn--sm" onClick={downloadTemplate}>Download template</button>
              <input type="file" accept=".csv,.xlsx" onChange={(e) => parseBulkFile(e.target.files[0])} />
            </div>
            {bulkRows.length > 0 && (
              <div className="fin-table-wrap">
                <table className="fin-table fin-table--compact">
                  <thead>
                    <tr>
                      {TABS[bulkType].columns.map((c) => (
                        <th key={c.key} className={c.money ? "fin-num" : ""}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((r, i) => (
                      <tr key={i}>
                        {TABS[bulkType].columns.map((c) => {
                          const val = c.render ? c.render(r) : r[c.key];
                          return (
                            <td key={c.key} className={c.money ? "fin-num" : ""}>
                              {c.money ? formatMoney(parseAmount(val)) : c.date ? formatDateMDY(val) : val}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="fin-modal__foot">
            <button className="fin-btn fin-btn--secondary" onClick={() => setShowBulk(false)} disabled={isBulkSaving}>Cancel</button>
            <button className="fin-btn fin-btn--primary" onClick={saveBulk} disabled={isBulkSaving || !bulkRows.length}>
              {isBulkSaving ? "Saving…" : `Save ${bulkRows.length} entr${bulkRows.length === 1 ? "y" : "ies"}`}
            </button>
          </div>
        </DepositsFinModal>
      )}
    </div>
  );
};

export default Expenses;
